
import { Interview } from "@/types/interview";
import { InterviewManagementService } from "./interviewManagementService";

export class InterviewManagementFacade {
  /**
   * Create a new interview
   */
  static async createInterview(interviewData: {
    intervieweeName: string;
    title: string;
    company: string;
    context: string;
    contextUrl?: string;
    question: string;
    answer: string;
    tags: string[];
    photoUrl?: string;
    publication_status?: string;
    isSponsored?: boolean;
    sponsorCompany?: string;
  }): Promise<{success: boolean, error?: string, id?: string}> {
    try {
      // Creating interview
      const result = await InterviewManagementService.createDirectInterview(interviewData);
      
      if (result.success) {
        return { success: true, id: result.id };
      } else {
        return { success: false, error: result.error || 'Failed to create interview' };
      }
    } catch (error) {
      console.error('[InterviewManagementFacade] Error creating interview:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get all pending interviews
   */
  static async getPendingInterviews(): Promise<Interview[]> {
    return InterviewManagementService.getPendingInterviews();
  }

  /**
   * Get all published interviews
   */
  static async getPublishedInterviews(): Promise<Interview[]> {
    return InterviewManagementService.getPublishedInterviews();
  }

  /**
   * Update interview details
   */
  static async updateInterview(interviewId: string, updateData: Partial<Interview>): Promise<boolean> {
    // Updating interview with provided data
    
    return InterviewManagementService.updateInterview(interviewId, updateData);
  }

  /**
   * Publish an interview
   */
  static async publishInterview(interviewId: string): Promise<boolean> {
    return InterviewManagementService.publishInterview(interviewId);
  }

  /**
   * Reject an interview
   */
  static async rejectInterview(interviewId: string): Promise<boolean> {
    return InterviewManagementService.rejectInterview(interviewId);
  }

  /**
   * De-publish an interview
   */
  static async depublishInterview(interviewId: string): Promise<boolean> {
    return InterviewManagementService.depublishInterview(interviewId);
  }

  /**
   * Delete an interview
   */
  static async deleteInterview(interviewId: string): Promise<boolean> {
    return InterviewManagementService.deleteInterview(interviewId);
  }

  /**
   * Get interview by ID
   */
  static async getInterviewById(interviewId: string): Promise<Interview | null> {
    return InterviewManagementService.getInterviewById(interviewId);
  }

  /**
   * Get all interviews marked for the feeder
   */
  static async getFeederInterviews(): Promise<Interview[]> {
    return InterviewManagementService.getFeederInterviews();
  }

  /**
   * Mark an interview for the feeder queue
   */
  static async markForFeeder(interviewId: string): Promise<boolean> {
    return InterviewManagementService.markForFeeder(interviewId);
  }
}
